// BaseLayout.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { initWebPush } from './pushClient';

import {
  Home,
  Users,
  Calendar,
  FileText,
  Clock,
  Settings,
  LogOut,
  CheckCircle,
  MessageCircle,
  Info,
  HelpCircle,
} from 'lucide-react';

import API from './api';
import MessageBell from './MessageBell';
import Messages from './Messages';

const teacherNav = [
  { name: 'Dashboard', to: '/teacher', icon: <Home size={18} /> },
  { name: 'My Visits', to: '/teacher/visits', icon: <Calendar size={18} /> },
  { name: 'My Students', to: '/teacher/students-info', icon: <Users size={18} /> },
  { name: 'My Students Skills', to: '/teacher/students', icon: <Users size={18} /> },
  { name: 'Students Skills (Substitute)', to: '/teacher/substitute', icon: <Users size={18} /> },
  { name: 'Student Goals & Achievement', to: '/teacher/achievement', icon: <CheckCircle size={18} /> },
  { name: 'My Availability', to: '/teacher/availability', icon: <Calendar size={18} /> },
  { name: 'My Students Availability', to: '/teacher/students-availability', icon: <Calendar size={18} /> },
  { name: 'My Day Planner', to: '/teacher/my-planner', icon: <Clock size={18} /> },
  { name: 'My Time Log', to: '/teacher/my-timelog', icon: <Clock size={18} /> },
  { name: 'Messages', to: '/teacher/messages', icon: <MessageCircle size={18} /> },
  { name: 'Document Management', to: '/teacher/docs', icon: <FileText size={18} /> },
  { name: 'About & Help', to: '/teacher/about', icon: <Info size={18} /> },
  { name: 'FAQ', to: '/teacher/faq', icon: <HelpCircle size={18} /> },
];

const adminNav = [
  { name: 'Dashboard', to: '/admin', icon: <Home size={18} /> },
  { name: 'Visits', to: '/admin/visits', icon: <Calendar size={18} /> },
  { name: 'Manage Visits', to: '/admin/manage-visits', icon: <Calendar size={18} /> },
  { name: 'Smart Visits Scheduler', to: '/admin/scheduler', icon: <Calendar size={18} /> },
  { name: 'Teacher Day Planner', to: '/admin/planer', icon: <Calendar size={18} /> },

  { name: 'Students', to: '/admin/students', icon: <Users size={18} /> },
  { name: 'Manage Students', to: '/admin/manage-students', icon: <Users size={18} /> },
  { name: 'Students Availability', to: '/admin/students-availability', icon: <Calendar size={18} /> },
  { name: 'Manage Teachers', to: '/admin/manage-teachers', icon: <Users size={18} /> },
  { name: 'Teachers Availability', to: '/admin/availability', icon: <Calendar size={18} /> },

  { name: 'Teachers Work Report', to: '/admin/work-report', icon: <FileText size={18} /> },
  { name: 'Student Progress Report', to: '/admin/student-progress', icon: <FileText size={18} /> },
  { name: 'Student Goals & Achievement', to: '/admin/achievement', icon: <CheckCircle size={18} /> },
  { name: 'Teacher Time Logs', to: '/admin/teacher-timelogs', icon: <Clock size={18} /> },
  { name: 'Document Management', to: '/admin/docs', icon: <FileText size={18} /> },

  { name: 'Messages', to: '/admin/messages', icon: <MessageCircle size={18} /> },
  { name: 'Settings', to: '/admin/settings', icon: <Settings size={18} /> },
  { name: 'Pricing', to: '/admin/pricing', icon: <Settings size={18} /> },

  { name: 'About & Help', to: '/admin/about', icon: <Info size={18} /> },
  { name: 'FAQ', to: '/admin/faq', icon: <HelpCircle size={18} /> },
];

const BaseLayout = ({ role: propRole = 'teacher', children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(
    () => JSON.parse(localStorage.getItem('user')) || {}
  );

  const [unreadCount, setUnreadCount] = useState(0);
  const [showMessagesModal, setShowMessagesModal] = useState(false);

  const displayName = user.name ? user.name : '';
  const role = user.role || propRole;
  const navItems = role === 'admin' ? adminNav : teacherNav;

  const handleLogout = () => {
    localStorage.clear();
    setUser({});
    navigate('/');
  };

  React.useEffect(() => {
    setUser(JSON.parse(localStorage.getItem('user')) || {});
  }, [location.pathname]);

  const refreshUnread = useCallback(async () => {
    try {
      const res = await API.get('/messages/channels');
      const chans = res.data || [];

      let count = 0;
      chans.forEach((ch) => {
        const lastMsg = ch.last_message_at;
        const lastRead = ch.last_read_at;
        if (lastMsg && (!lastRead || lastMsg > lastRead)) {
          count += 1;
        }
      });

      setUnreadCount(count);
    } catch (e) {
      console.error('Failed to fetch unread messages', e);
    }
  }, []);

  React.useEffect(() => {
    refreshUnread();
    const interval = setInterval(refreshUnread, 10000);
    return () => clearInterval(interval);
  }, [refreshUnread]);

  const toggleMessagesModal = () => {
    setShowMessagesModal((prev) => !prev);
  };

  // Helper za nazive sekcija po indexu
  const getSectionLabel = (index) => {
    if (role === 'teacher') {
      if (index === 0) return 'Daily work';
      if (index === 6) return 'Availability & planning';
      if (index === 10) return 'Communication & documents';
      if (index === 12) return 'Help & info';
    } else {
      // admin
      if (index === 0) return 'Operations';
      if (index === 5) return 'Students & teachers';
      if (index === 10) return 'Reports & analytics';
      if (index === 15) return 'Communication & settings';
      if (index === 18) return 'Help & info';
    }
    return null;
  };
  
  useEffect(() => {
    const token = localStorage.getItem('token');
    const u = JSON.parse(localStorage.getItem('user') || '{}');

    // samo ako imamo user-a i token
    if (token && u && u.id) {
      initWebPush();
    }
  }, []);
  

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Mobile sidebar overlay */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed z-40 inset-y-0 left-0 w-64 bg-sky-900 text-white transform ${
          open ? 'translate-x-0' : '-translate-x-full'
        } transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 lg:flex`}
      >
        <div className="flex flex-col w-64 h-full overflow-y-auto">
          {/* HEADER */}
          <div className="flex items-center justify-between p-3 border-b border-sky-700">
            <Link
              to={role === 'admin' ? '/admin' : '/teacher'}
              className="flex flex-col"
            >
              <span className="text-sm font-semibold tracking-wide">
                VisitOMS EasyCare
              </span>
              <span className="text-[11px] text-sky-100">
                {role === 'admin' ? 'Admin Dashboard' : 'Teacher Dashboard'}
              </span>
            </Link>
            <button className="lg:hidden" onClick={() => setOpen(false)}>
              ✕
            </button>
          </div>

          {/* NAV ITEMS */}
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item, index) => {
              const isActive = location.pathname === item.to;
              const isMessagesLink = item.to.endsWith('/messages');
              const sectionLabel = getSectionLabel(index);

              return (
                <React.Fragment key={item.name}>
                  {sectionLabel && (
                    <div className="mt-1 mb-1">
                      <div className="text-[10px] uppercase tracking-wide text-sky-100 opacity-70">
                        {sectionLabel}
                      </div>
                      <div className="h-px bg-sky-800 opacity-60 mt-1" />
                    </div>
                  )}

                  <Link
                    to={item.to}
                    className={`flex items-center gap-2 px-3 py-2 rounded transition ${
                      isActive ? 'bg-sky-800 font-semibold' : 'hover:bg-sky-700'
                    }`}
                  >
                    {item.icon}
                    <div className="flex-1 flex items-center justify-between">
                      <span>{item.name}</span>
                      {isMessagesLink && unreadCount > 0 && (
                        <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] bg-purple-500 text-white">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </div>
                  </Link>
                </React.Fragment>
              );
            })}

            {/* LOGOUT */}
            <button
              onClick={handleLogout}
              className="mt-4 flex items-center gap-2 px-3 py-2 rounded hover:bg-sky-700 transition w-full text-left"
            >
              <LogOut size={18} /> Logout
            </button>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-y-auto h-screen">
        {/* Header */}
        <div className="bg-white shadow px-4 py-3 flex items-center justify-between sticky top-0 z-20">
          <h2 className="font-semibold text-lg">
            {displayName
              ? `${displayName} - ${role === 'admin' ? 'Admin' : 'Teacher'}`
              : role === 'admin'
              ? 'Admin Panel'
              : 'Teacher Dashboard'}
            <span className="ml-2 text-xs text-gray-500">
              VisitOMS EasyCare v1.0
            </span>
          </h2>

          <div className="flex items-center gap-3">
            <MessageBell role={role} count={unreadCount} onClick={toggleMessagesModal} />

            {/* mobile hamburger */}
            <button
              className="lg:hidden text-2xl"
              onClick={() => setOpen(!open)}
            >
              ☰
            </button>
          </div>
        </div>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-4">{children}</main>
      </div>

      {showMessagesModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-5xl h-[80vh] max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-2 border-b">
              <h3 className="font-semibold text-sm">Messages</h3>
              <button
                className="text-gray-500 hover:text-gray-800 text-lg"
                onClick={toggleMessagesModal}
              >
                ✕
              </button>
            </div>

            <div className="flex-1 min-h-0 p-3">
              <Messages
                mode={role === 'admin' ? 'admin' : 'teacher'}
                onRefreshUnread={refreshUnread}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BaseLayout;




